<template>
  <div>
    <!-- List view -->
    <div v-if="!editing">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold text-gray-800">Form Templates</h2>
        <button
          @click="startNew"
          class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >New Template</button>
      </div>

      <div v-if="templates.length === 0" class="text-gray-500 text-sm py-8 text-center">
        No templates yet.
      </div>

      <div class="space-y-3">
        <div
          v-for="t in templates"
          :key="t.id"
          class="border border-gray-200 rounded-lg p-4 flex items-center justify-between"
        >
          <div>
            <div class="font-medium text-gray-800">{{ t.name }}</div>
            <div class="text-sm text-gray-500">{{ t.fields.length }} field{{ t.fields.length !== 1 ? 's' : '' }}</div>
          </div>
          <div class="flex items-center gap-2">
            <button
              @click="startEdit(t)"
              class="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >Edit</button>
            <button
              @click="confirmDelete(t)"
              class="px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-md hover:bg-red-50"
            >Delete</button>
          </div>
        </div>
      </div>

      <!-- Delete confirmation -->
      <div v-if="deleteConfirm" class="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
          <h3 class="font-semibold text-gray-800 mb-2">Delete Template</h3>
          <p class="text-sm text-gray-600 mb-4">
            Are you sure you want to delete "{{ deleteConfirm.name }}"? This cannot be undone.
          </p>
          <div v-if="error" class="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {{ error }}
          </div>
          <div class="flex justify-end gap-2">
            <button
              @click="deleteConfirm = null; error = null"
              class="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >Cancel</button>
            <button
              @click="doDelete"
              class="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
            >Delete</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit view -->
    <div v-else>
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold text-gray-800">
          {{ editingIsNew ? 'New Template' : 'Edit Template' }}
        </h2>
      </div>

      <div v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
        {{ error }}
      </div>

      <!-- Template name -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
        <input
          v-model="templateName"
          type="text"
          placeholder="e.g. Intake Form"
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <!-- Fields -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">Fields</label>

        <div v-if="fields.length === 0" class="text-gray-400 text-sm py-4 text-center border border-dashed border-gray-300 rounded-lg">
          No fields yet. Add one below.
        </div>

        <div class="space-y-3">
          <div
            v-for="(field, idx) in fields"
            :key="idx"
            class="border border-gray-200 rounded-lg p-3"
          >
            <div class="flex items-start gap-3">
              <!-- Reorder buttons -->
              <div class="flex flex-col gap-0.5 pt-1">
                <button
                  @click="moveField(idx, -1)"
                  :disabled="idx === 0"
                  class="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs leading-none"
                  title="Move up"
                >&uarr;</button>
                <button
                  @click="moveField(idx, 1)"
                  :disabled="idx === fields.length - 1"
                  class="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs leading-none"
                  title="Move down"
                >&darr;</button>
              </div>

              <!-- Field config -->
              <div class="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <!-- Label -->
                <div>
                  <label class="block text-xs text-gray-500 mb-0.5">Label</label>
                  <input
                    v-model="field.label"
                    @blur="autoKey(field)"
                    type="text"
                    placeholder="Field label"
                    class="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <!-- Type -->
                <div>
                  <label class="block text-xs text-gray-500 mb-0.5">Type</label>
                  <select
                    v-model="field.type"
                    class="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="short_text">Short Text</option>
                    <option value="long_text">Long Text</option>
                    <option value="select">Select</option>
                    <option value="multi_select">Multi-Select</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="date">Date</option>
                    <option value="number">Number</option>
                  </select>
                </div>

                <!-- Key -->
                <div>
                  <label class="block text-xs text-gray-500 mb-0.5">Key</label>
                  <input
                    v-if="field.keyEditing"
                    v-model="field.key"
                    @blur="field.keyEditing = false; field.keyEdited = true"
                    type="text"
                    class="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <div
                    v-else
                    @click="field.keyEditing = true"
                    class="w-full border border-gray-200 bg-gray-50 rounded px-2 py-1.5 text-sm font-mono text-gray-600 cursor-pointer hover:bg-gray-100"
                    title="Click to edit"
                  >{{ field.key || '(auto)' }}</div>
                </div>

                <!-- Required toggle -->
                <div class="flex items-end pb-1">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" v-model="field.required" class="rounded" />
                    <span class="text-sm text-gray-700">Required</span>
                  </label>
                </div>

                <!-- Options (for select/multi_select) -->
                <div v-if="field.type === 'select' || field.type === 'multi_select'" class="sm:col-span-2">
                  <label class="block text-xs text-gray-500 mb-0.5">Options (comma-separated)</label>
                  <input
                    v-model="field.optionsText"
                    type="text"
                    placeholder="Option 1, Option 2, Option 3"
                    class="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <!-- Conditional visibility (showWhen) -->
                <div class="sm:col-span-2">
                  <label class="flex items-center gap-2 cursor-pointer text-sm text-gray-700 mb-1">
                    <input type="checkbox" v-model="field.showWhenEnabled" class="rounded" />
                    <span>Show conditionally</span>
                  </label>
                  <div v-if="field.showWhenEnabled && fieldsBeforeIndex(idx).length > 0" class="flex items-center gap-2 mt-1 flex-wrap">
                    <span class="text-xs text-gray-500">Show when</span>
                    <select
                      v-model="field.showWhenField"
                      class="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">-- Field --</option>
                      <option v-for="f in fieldsBeforeIndex(idx)" :key="f.key" :value="f.key">{{ f.label }}</option>
                    </select>
                    <select
                      v-model="field.showWhenOp"
                      class="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="eq">is</option>
                      <option value="neq">is not</option>
                    </select>
                    <!-- Value: dropdown for select/checkbox source, text input otherwise -->
                    <select
                      v-if="showWhenSourceOptions(field)"
                      v-model="field.showWhenValue"
                      class="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">-- Value --</option>
                      <option v-for="opt in showWhenSourceOptions(field)" :key="String(opt.value)" :value="opt.value">{{ opt.label }}</option>
                    </select>
                    <input
                      v-else
                      v-model="field.showWhenValue"
                      type="text"
                      placeholder="Value"
                      class="border border-gray-300 rounded px-2 py-1 text-sm w-32 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div v-else-if="field.showWhenEnabled && fieldsBeforeIndex(idx).length === 0" class="text-xs text-gray-400 mt-1">
                    Add fields above this one to create conditions.
                  </div>
                </div>
              </div>

              <!-- Remove button -->
              <button
                @click="removeField(idx)"
                class="text-gray-400 hover:text-red-500 text-lg leading-none pt-1"
                title="Remove field"
              >&times;</button>
            </div>
          </div>
        </div>

        <button
          @click="addField"
          class="mt-3 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
        >+ Add Field</button>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-3 pt-4 border-t border-gray-200">
        <button
          @click="save"
          :disabled="saving"
          class="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >{{ saving ? 'Saving...' : 'Save' }}</button>
        <button
          @click="cancelEdit"
          class="px-5 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
        >Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TemplateBuilder',
  props: {
    locationId: { type: String, required: true },
    templates: { type: Array, required: true }
  },
  emits: ['templates-changed'],
  data() {
    return {
      editing: null,
      editingIsNew: false,
      templateName: '',
      fields: [],
      saving: false,
      error: null,
      deleteConfirm: null
    }
  },
  methods: {
    startNew() {
      this.editing = {}
      this.editingIsNew = true
      this.templateName = ''
      this.fields = []
      this.error = null
    },
    startEdit(template) {
      this.editing = template
      this.editingIsNew = false
      this.templateName = template.name
      this.fields = template.fields.map(f => ({
        label: f.label,
        key: f.key,
        type: f.type,
        required: f.required || false,
        optionsText: (f.options || []).join(', '),
        keyEdited: true,
        keyEditing: false,
        showWhenEnabled: !!f.showWhen,
        showWhenField: f.showWhen?.field || '',
        showWhenOp: f.showWhen?.op || 'eq',
        showWhenValue: f.showWhen?.value ?? ''
      }))
      this.error = null
    },
    cancelEdit() {
      this.editing = null
      this.error = null
    },
    addField() {
      this.fields.push({
        label: '',
        key: '',
        type: 'short_text',
        required: false,
        optionsText: '',
        keyEdited: false,
        keyEditing: false,
        showWhenEnabled: false,
        showWhenField: '',
        showWhenOp: 'eq',
        showWhenValue: ''
      })
    },
    removeField(idx) {
      this.fields.splice(idx, 1)
    },
    moveField(idx, direction) {
      const newIdx = idx + direction
      if (newIdx < 0 || newIdx >= this.fields.length) return
      const temp = this.fields[idx]
      this.fields.splice(idx, 1)
      this.fields.splice(newIdx, 0, temp)
    },
    autoKey(field) {
      if (field.keyEdited) return
      field.key = field.label
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
    },
    buildFields() {
      return this.fields.map(f => {
        const out = {
          label: f.label.trim(),
          key: f.key.trim(),
          type: f.type,
          required: f.required
        }
        if (f.type === 'select' || f.type === 'multi_select') {
          out.options = f.optionsText
            .split(',')
            .map(o => o.trim())
            .filter(o => o)
        }
        if (f.showWhenEnabled && f.showWhenField) {
          out.showWhen = {
            field: f.showWhenField,
            op: f.showWhenOp,
            value: f.showWhenValue
          }
        }
        return out
      })
    },
    fieldsBeforeIndex(idx) {
      return this.fields.slice(0, idx).filter(f => f.key.trim())
    },
    showWhenSourceOptions(field) {
      const source = this.fields.find(f => f.key === field.showWhenField)
      if (!source) return null
      if (source.type === 'select' || source.type === 'multi_select') {
        const opts = source.optionsText.split(',').map(o => o.trim()).filter(o => o)
        return opts.map(o => ({ label: o, value: o }))
      }
      if (source.type === 'checkbox') {
        return [{ label: 'Checked', value: true }, { label: 'Unchecked', value: false }]
      }
      return null
    },
    validateClient() {
      if (!this.templateName.trim()) return 'Template name is required'
      if (this.fields.length === 0) return 'At least one field is required'
      const keys = new Set()
      for (let i = 0; i < this.fields.length; i++) {
        const f = this.fields[i]
        if (!f.label.trim()) return `Field ${i + 1}: label is required`
        if (!f.key.trim()) return `Field ${i + 1}: key is required`
        if (keys.has(f.key.trim())) return `Field ${i + 1}: duplicate key "${f.key.trim()}"`
        keys.add(f.key.trim())
        if ((f.type === 'select' || f.type === 'multi_select')) {
          const opts = f.optionsText.split(',').map(o => o.trim()).filter(o => o)
          if (opts.length === 0) return `Field ${i + 1}: select/multi_select requires at least one option`
        }
      }
      return null
    },
    async save() {
      this.error = null
      const clientError = this.validateClient()
      if (clientError) {
        this.error = clientError
        return
      }

      this.saving = true
      try {
        const body = {
          locationId: this.locationId,
          name: this.templateName.trim(),
          fields: this.buildFields()
        }

        let res
        if (this.editingIsNew) {
          res = await fetch('/opsflow/templates', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          })
        } else {
          res = await fetch(`/opsflow/templates/${this.editing.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          })
        }

        if (!res.ok) {
          const data = await res.json()
          this.error = data.error || 'Failed to save template'
          return
        }

        this.editing = null
        this.$emit('templates-changed')
      } catch (e) {
        this.error = 'Network error saving template'
      } finally {
        this.saving = false
      }
    },
    confirmDelete(template) {
      this.deleteConfirm = template
      this.error = null
    },
    async doDelete() {
      if (!this.deleteConfirm) return
      try {
        const res = await fetch(
          `/opsflow/templates/${this.deleteConfirm.id}?locationId=${encodeURIComponent(this.locationId)}`,
          { method: 'DELETE' }
        )
        if (!res.ok) {
          const data = await res.json()
          this.error = data.error || 'Failed to delete template'
          return
        }
        this.deleteConfirm = null
        this.error = null
        this.$emit('templates-changed')
      } catch (e) {
        this.error = 'Network error deleting template'
      }
    }
  }
}
</script>
